import { ENC } from "@/constants/testIds";
import { NA } from "@/lib/patientChart";
import { looksLikeNoteRtf, noteContentToEditorHtml } from "@/lib/noteRtf";

function NotePrintBody({ content }) {
  const text = content || " ";
  if (looksLikeNoteRtf(text)) {
    return (
      <div
        className="note-print-body note-print-body--rtf"
        dangerouslySetInnerHTML={{ __html: noteContentToEditorHtml(text) || " " }}
      />
    );
  }
  return <pre className="note-print-body">{text}</pre>;
}

function Fact({ label, value }) {
  if (!value || value === NA || value === "—") return null;
  return (
    <div className="note-print-fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function TemplatedNotePrintDocument({ doc }) {
  const pages = doc.reportShell?.pages || [];
  return (
    <article
      data-testid={ENC.screenNotePrintDocument}
      className="note-print-root note-print-root--templated"
      aria-hidden="true"
    >
      {pages.map((page) => (
        <section
          key={page.pageNumber}
          className="note-print-page"
          data-page={page.pageNumber}
          data-testid={
            page.pageNumber === 1
              ? ENC.screenNotePrintFirstPage
              : ENC.screenNotePrintSubsequentPage
          }
        >
          <header
            className={
              page.pageNumber === 1
                ? "note-print-template-header"
                : "note-print-template-header note-print-template-header--subsequent"
            }
            dangerouslySetInnerHTML={{ __html: page.headerHtml || "" }}
          />
          <div className="note-print-page__body">
            <NotePrintBody content={page.content} />
          </div>
          <footer
            className="note-print-template-footer"
            dangerouslySetInnerHTML={{ __html: page.footerHtml || "" }}
          />
        </section>
      ))}
    </article>
  );
}

export default function NotePrintDocument({ document: doc }) {
  if (!doc) return null;
  if (doc.reportShell?.pages?.length) {
    return <TemplatedNotePrintDocument doc={doc} />;
  }
  return (
    <article
      data-testid={ENC.screenNotePrintDocument}
      className="note-print-root"
      aria-hidden="true"
    >
      <header className="note-print-header">
        <p className="note-print-kicker">EMRbyMD</p>
        <h1>{doc.noteType}</h1>
        {doc.title ? <p className="note-print-title">{doc.title}</p> : null}
      </header>

      <section className="note-print-section">
        <h2>Patient</h2>
        <dl>
          <Fact label="Name" value={doc.patientName} />
          <Fact label="DOB" value={doc.dob} />
          <Fact label="MRN" value={doc.mrn} />
        </dl>
      </section>

      <section className="note-print-section">
        <h2>Note</h2>
        <dl>
          <Fact label="Encounter #" value={doc.encounterNumber} />
          <Fact label="Date of service" value={doc.dateOfService} />
          <Fact label="Note date" value={doc.noteDate} />
          <Fact label="Type" value={doc.noteType} />
          <Fact label="Author" value={doc.author} />
          <Fact label="Status" value={doc.statusLabel} />
        </dl>
      </section>

      <section className="note-print-section">
        <h2>Note content</h2>
        <NotePrintBody content={doc.content} />
      </section>
    </article>
  );
}
